import { useState, useEffect } from "react";
import App from "./App";
import AuthScreen from "./AuthScreen";
import OnboardingScreen from "./OnboardingScreen";
import { supabase } from "../lib/supabase";

async function loadProfile(userId) {
  try {
    const { data } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    return data || null;
  } catch {
    return null;
  }
}

function isIncomplete(p) {
  return !p || !p.handle || !p.interests || p.interests.length === 0;
}

export default function Root() {
  const [screen, setScreen] = useState("loading");
  const [user,   setUser]   = useState(null);

  useEffect(() => {
    // Single source of truth: check the actual session on mount
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session?.user) { setScreen("guest"); return; }
      const profile = await loadProfile(session.user.id);
      if (isIncomplete(profile)) { setScreen("onboarding"); }
      else { setUser(profile); setScreen("app"); }
    }).catch(() => setScreen("guest"));

    // Only react to SIGNED_IN (new login) — ignore SIGNED_OUT to prevent
    // spurious kick-outs from supabase-js internal token handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event !== "SIGNED_IN") return;
        if (!session?.user) return;
        const profile = await loadProfile(session.user.id);
        if (isIncomplete(profile)) { setScreen("onboarding"); }
        else { setUser(profile); setScreen("app"); }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const handleAuthSuccess = (userData, isNew) => {
    // onAuthStateChange SIGNED_IN handles navigation — this is just a fallback
    if (isNew) setScreen("onboarding");
  };

  const handleOnboardingComplete = async (userData) => {
    fetch("/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "welcome", record: userData }),
    }).catch(() => {});
    if (userData?.id) {
      const profile = await loadProfile(userData.id);
      if (profile) setUser(profile);
    }
    setScreen("app");
  };

  const handleSignOut = () => setScreen("guest");
  const handleSignIn  = () => setScreen("auth");

  if (screen === "loading")
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"'Noto Sans',sans-serif"}}>
        <div style={{fontSize:24,fontWeight:900,letterSpacing:"-0.05em"}}>
          nearme<span style={{color:"#FF0000"}}>.</span>
        </div>
      </div>
    );

  if (screen === "auth")
    return <AuthScreen onSuccess={handleAuthSuccess} />;

  if (screen === "onboarding")
    return <OnboardingScreen user={user} onComplete={handleOnboardingComplete} />;

  const path = window.location.pathname.replace(/^\//, '') || '';
  const initialListingId = path.startsWith('listing/') ? path.slice(8) : null;
  const initialHandle = !initialListingId && path ? path : null;

  if (screen === "guest")
    return <App guest={true} profile={null} onSignIn={handleSignIn} onSignOut={handleSignOut} initialHandle={initialHandle} initialListingId={initialListingId} />;

  return <App profile={user} onSignOut={handleSignOut} initialHandle={initialHandle} initialListingId={initialListingId} />;
}
